import {
  ActionIcon as MantineActionIcon,
  Button as MantineButton,
  CopyButton as MantineCopyButton,
  Loader as MantineLoader,
} from "@mantine/core";
import classNames from "classnames";
import * as React from "react";
import type { UrlObject } from "url";
import type { ElementProps, SizeValue, StyleProps } from "../primitives/style-props";

import classes from "./buttons.module.css";

/**
 * Gradient fill for `variant="gradient"`. Our own spelling of the value Mantine accepts; one call
 * site (`AIButton`) uses it.
 */
export interface GradientValue {
  from: string;
  to: string;
  deg?: number;
}

export interface ButtonProps extends ElementProps<"button">, StyleProps {
  children?: React.ReactNode;
  variant?: "filled" | "light" | "outline" | "subtle" | "default" | "white" | "gradient";
  color?: string;
  /** Non-numeric: Mantine resolves Button size by theme key only, so a number would silently produce no size. */
  size?: Exclude<SizeValue, number>;
  radius?: SizeValue;
  /** Reduced vertical and horizontal padding. */
  compact?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** Gradient fill, read only when `variant="gradient"`. */
  gradient?: GradientValue;
  /**
   * The root element or routing component (`"a"`, `"span"`, `Link`, `OrgLink` in this repo). Open
   * `ElementType` like `BadgeProps.component`, not the `AnchorProps` intrinsic/routing split:
   * several shipped components derive their props with `Omit<ButtonProps, …>`, and `Omit` over an
   * intersection-of-union flattens the split away, so the obligation it would carry cannot survive
   * the derivation anyway. `href` below exists for the link roots.
   */
  component?: React.ElementType;
  href?: string | UrlObject;
  target?: string;
  rel?: string;
}

/**
 * The props the Button cast routes to Mantine 7, which renamed `leftIcon`/`rightIcon` to
 * `leftSection`/`rightSection` and folded `compact` into its `compact-<size>` size keys. The owned
 * names are unchanged; the wrapper maps them and draws `loading` itself (SC-2703).
 */
type ButtonRootProps = Omit<ButtonProps, "compact" | "leftIcon" | "rightIcon" | "loading"> & {
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
};

/**
 * Same polymorphic flattening as `UnstyledButtonRoot` in the primitives buttons module: Mantine
 * types `Button` as a polymorphic union whose `component` arm selects the element prop set, and a
 * non-generic wrapper cannot restate that. `controls-casts.test.ts` pins the own-prop keys the cast
 * routes to Mantine; the intrinsic `<button>` props reach Mantine through the polymorphic rest-prop
 * spread and rest on the cast alone.
 */
const ButtonRoot = MantineButton as React.ComponentType<ButtonRootProps & { readonly ref?: React.Ref<HTMLButtonElement> }>;

/**
 * Mantine 6 drew `loading` at its default `loaderPosition="left"`: the loader took the left icon's
 * place, half the uncompacted button height, in the variant's text color, and the label stayed.
 * Mantine 7 has no loader position, so the loader goes into the left section here.
 */
const buttonLoader = (size: ButtonProps["size"]): React.JSX.Element => (
  <MantineLoader color="var(--button-color)" size={`calc(var(--button-height-${size ?? "sm"}, var(--button-height)) / 2)`} />
);

/**
 * The owned root classes: `control` gives a disabled root Mantine 6's `pointer-events: none`, which
 * Mantine 7 dropped (SC-2703); `loading` draws Mantine 6's loading wash; `component` is the Button's or
 * the ActionIcon's own Mantine 6 root display and disabled colors. See `buttons.module.css`.
 */
const rootClassName = (component: string | undefined, className: string | undefined, loading: boolean | undefined): string =>
  classNames(className, classes.control, component, loading && classes.loading);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { children, compact, leftIcon, rightIcon, loading, size, className, ...props },
  ref,
) {
  return (
    <ButtonRoot
      {...props}
      size={compact ? `compact-${size ?? "sm"}` : size}
      leftSection={loading ? buttonLoader(size) : leftIcon}
      rightSection={rightIcon}
      className={rootClassName(classes.buttonRoot, className, loading)}
      data-sc-print-hidden="true"
      ref={ref}
    >
      {children}
    </ButtonRoot>
  );
});

export interface ActionIconProps extends ElementProps<"button">, StyleProps {
  /** The icon. */
  children?: React.ReactNode;
  variant?: "subtle" | "transparent" | "light" | "filled" | "outline" | "default" | "gradient";
  color?: string;
  size?: SizeValue;
  radius?: SizeValue;
  loading?: boolean;
  /** Gradient fill, read only when `variant="gradient"`. */
  gradient?: GradientValue;
  /** Open for the same `Omit`-derivation reason as `ButtonProps.component` above. */
  component?: React.ElementType;
  href?: string | UrlObject;
  target?: string;
  rel?: string;
}

/** Same polymorphic flattening as `ButtonRoot` above; `loading` is drawn by the wrapper, not routed. */
const ActionIconRoot = MantineActionIcon as React.ComponentType<
  Omit<ActionIconProps, "loading"> & { readonly ref?: React.Ref<HTMLButtonElement> }
>;

/**
 * Mantine 6 defaulted ActionIcon to a gray `subtle` button; Mantine 7 defaults to a filled button in
 * the primary color, so the owned defaults stay Mantine 6's (SC-2703). `loading` swaps the icon for a
 * loader 70% of the button's size, as Mantine 6 did.
 */
export const ActionIcon = React.forwardRef<HTMLButtonElement, ActionIconProps>(function ActionIcon(
  { children, loading, variant = "subtle", color = "gray", className, ...props },
  ref,
) {
  return (
    <ActionIconRoot
      {...props}
      variant={variant}
      color={color}
      className={rootClassName(classes.actionIconRoot, className, loading)}
      ref={ref}
    >
      {loading ? <MantineLoader color="var(--ai-color)" size="calc(var(--ai-size) * 0.7)" /> : children}
    </ActionIconRoot>
  );
});

export interface CopyButtonProps {
  /** Value copied to the clipboard. */
  value: string;
  /** Duration copied state remains true, in milliseconds. */
  timeout?: number;
  children: (payload: { copied: boolean; copy: () => void }) => React.ReactNode;
}

export const CopyButton = ({ children, ...props }: CopyButtonProps): React.JSX.Element => (
  <MantineCopyButton {...props}>{children}</MantineCopyButton>
);
